import { SYSTEM_COLLAPSE } from 'common/actions/row-groups/types';
import { COMMON_STOCK, CONVERTIBLE_NOTES, PREFERRED_STOCK } from 'common/constants/securityTypes';

export const CAP_TABLE_TITLE = 'Cap Table';
export const CAP_TABLE_SIDEBAR_TITLE = 'Capitalization Table';
export const CAP_TABLE_SLUG = 'cap-table';
export const FUND_OWNERSHIP_TITLE = 'Fund Ownership';
export const FUND_OWNERSHIP_SLUG = 'fund-ownership';
export const EMPTY_BREAKPOINT = 'Breakpoint column cannot be empty';
export const BREAKPOINT_ANALYSIS_TITLE = 'Breakpoint Analysis';
export const BREAKPOINT_ANALYSIS_SLUG = 'breakpoint-analysis';
export const CASH_FLOW_LEDGER_TITLE = 'Cash Flow Ledger';
export const CASH_FLOW_LEDGER_SLUG = 'cash-flow-ledger';
export const BREAKPOINT_INFINITY = 'Infinity';
export const BREAKPOINT_PRO_RATA = 'Pro Rata';
export const BREAKPOINT_RANGE = 'breakpoint-range';
export const BREAKPOINT_TITLE = 'breakpoint-title';
export const BREAKPOINT_PRICE_ALIAS = 'breakpoint_price';
export const ALLOCATION_PERCENTAGE = 'allocation_percentage';
export const BREAKPOINT_SERIE_ALIAS = 'breakpoint_serie';
export const BREAKPOINT_TOTAL_ALIAS = 'breakpoint_total';
export const TWO_DECIMAL_PLACES = 2;
export const MAX_DECIMAL_PLACES_WHEN_EDITING_BPS = 4;

export const CAP_TABLE_TAB_SLUGS: Set<string> = new Set([
  CAP_TABLE_SLUG,
  FUND_OWNERSHIP_SLUG,
  BREAKPOINT_ANALYSIS_SLUG,
  CASH_FLOW_LEDGER_SLUG
]);

// Cap-table grid row positions. These *_ROW_NUMBER constants are the SINGLE
// SOURCE OF TRUTH for where each field sits in the main securities grid.
// Spreadsheet formulas (expr/altExpr) and selectors must interpolate these
// constants (e.g. `@${ISSUE_PRICE_ROW_NUMBER}`), never hardcode `@N`.
// Inserting or moving a grid row means renumbering the values HERE ONLY -
// every interpolated reference then follows automatically.
export const SECURITY = 'Security';
export const SECURITIES = 'Securities';
export const SECURITY_NAME_ALIAS = 'name';
export const SECURITY_NAME_TITLE = 'Security Name';
export const SECURITY_NAME_ROW_NUMBER = 1;

export const LEGAL_NAME_ALIAS = 'legal_name';
export const LEGAL_NAME_TITLE = 'Legal Name';
export const LEGAL_NAME_ROW_NUMBER = 2;

export const INVESTMENT_DATE_ALIAS = 'investment_date';
export const INVESTMENT_DATE_TITLE = 'Investment Date';
export const INVESTMENT_DATE_ROW_NUMBER = 3;

export const SECURITY_TYPE_ROW_NUMBER = 4;
export const SECURITY_TYPE_ALIAS = 'security_type';
export const SECURITY_TYPE_TITLE = 'Security Type';

export const ISSUE_PRICE_ALIAS = 'issue_price';
export const ISSUE_PRICE_TITLE = 'Original Issue Price';
export const ISSUE_PRICE_ROW_NUMBER = 5;

export const SHARES_OUTSTANDING_ALIAS = 'shares_outstanding';
export const SHARES_OUTSTANDING_TITLE = 'Shares Outstanding';
export const SHARES_OUTSTANDING_ROW_NUMBER = 6;

export const CONVERSION_RATE_ALIAS = 'conversion_rate';
export const CONVERSION_RATE_TITLE = 'Conversion Rate';
export const CONVERSION_RATE_ROW_NUMBER = 7;

export const PIK_SHARES_ALIAS = 'pik_shares';
export const PIK_SHARES_TITLE = 'Pik Shares (As of Today)';
export const PIK_SHARES_ROW_NUMBER = 8;

export const SHARES_FULLY_DILUTED_ALIAS = 'shares_diluted';
export const SHARES_FULLY_DILUTED_TITLE = 'Shares Fully Diluted';
export const SHARES_FULLY_DILUTED_ROW_NUMBER = 9;

export const CT_ONE_DECIMAL_ROW_NUMBERS = new Set([
  SHARES_OUTSTANDING_ROW_NUMBER,
  PIK_SHARES_ROW_NUMBER,
  SHARES_FULLY_DILUTED_ROW_NUMBER,
]);

export const CT_ONE_DECIMAL_DISPLAY_DECIMALS = 1;

export const CURRENT_OWNERSHIP_ALIAS = 'current_own_percent';
export const CURRENT_OWNERSHIP_TITLE = 'Current Ownership';
export const CURRENT_OWNERSHIP_ROW_NUMBER = 10;

export const FULLY_DILUTED_OWNERSHIP_ALIAS = 'diluted_own_percent';
export const FULLY_DILUTED_OWNERSHIP_TITLE = 'Fully Diluted Ownership';
export const FULLY_DILUTED_OWNERSHIP_ROW_NUMBER = 11;

export const STRIKE_PRICE_ALIAS = 'strike_price';
export const STRIKE_PRICE_TITLE = 'Strike Price';
export const STRIKE_PRICE_ROW_NUMBER = 12;

export const UNDERLYING_SECURITY_ALIAS = 'underlying_security_ref';
export const UNDERLYING_SECURITY_TITLE = 'Underlying Security';
export const UNDERLYING_SECURITY_ROW_NUMBER = 13;

export const PREFERRED_TERMS_ALIAS = 'prefered_terms';
export const PREFERRED_TERMS_TITLE = 'Preferred Terms';

export const FIRM_SHARES_ALIAS = 'firm_shares';
export const FIRM_OWNERSHIP_PERCENTAGE_ALIAS = 'firm_ownership_percentage';
export const FIRM_LIQUIDATION_PREFERENCE_ALIAS = 'firm_liquidation_preference';
export const PREFERRED_TERMS_ROW_NUMBER = 14;

export const LIQUIDATION_PREFERENCE_ALIAS = 'liquidation_preference';
export const LIQUIDATION_PREFERENCE_TITLE = 'Liquidation Preference';
export const LIQUIDATION_PREFERENCE_ROW_NUMBER = 15;

export const SENIORITY_ALIAS = 'seniority';
export const SENIORITY_TITLE = 'Liquidation Priority';
export const SELECT_PRIORITY = 'SELECT PRIORITY';
export const SENIORITY_ROW_NUMBER = 16;

export const PARTICIPATION_ALIAS = 'is_participating';
export const PARTICIPATION_TITLE = 'Participating';
export const PARTICIPATION_ROW_NUMBER = 17;

export const IS_PARTICIPATION_CAP_ALIAS = 'is_participation_cap';
export const IS_PARTICIPATION_CAP_TITLE = 'Participation Cap';
export const IS_PARTICIPATION_CAP_ROW_NUMBER = 18;

export const PARTICIPATION_CAP_ALIAS = 'participation_cap';
export const PARTICIPATION_CAP_TITLE = 'Participation Cap (X)';
export const PARTICIPATION_CAP_ROW_NUMBER = 19;

export const CUMULATIVE_DIVIDENDS_ALIAS = 'has_dividends';
export const CUMULATIVE_DIVIDENDS_TITLE = 'Cumulative Dividends';
export const CUMULATIVE_DIVIDENDS_ROW_NUMBER = 20;

export const HAS_MULTIPLE_INVESTMENT_DATES_ALIAS = 'has_multiple_investments';
export const HAS_MULTIPLE_INVESTMENT_DATES_TITLE = 'Multiple Investments Dates';
export const HAS_MULTIPLE_INVESTMENT_DATES_ROW_NUMBER = 21;

export const DIVIDEND_PAYMENT_TYPE_ALIAS = 'dividend_payment_type';
export const DIVIDEND_PAYMENT_TYPE_TITLE = 'Cash or Pik';
export const DIVIDEND_PAYMENT_TYPE_ROW_NUMBER = 22;

export const DIVIDEND_RATE_ALIAS = 'dividend_rate';
export const DIVIDEND_RATE_TITLE = 'Dividend Rate';
export const DIVIDEND_RATE_ROW_NUMBER = 23;

export const COMPOUNDING_ALIAS = 'has_compounding';
export const COMPOUNDING_TITLE = 'Compounding';
export const COMPOUNDING_ROW_NUMBER = 24;

export const BOOLEAN_FIELD_ALIASES = new Set([
  PARTICIPATION_ALIAS,
  IS_PARTICIPATION_CAP_ALIAS,
  CUMULATIVE_DIVIDENDS_ALIAS,
  HAS_MULTIPLE_INVESTMENT_DATES_ALIAS,
  COMPOUNDING_ALIAS,
]);

export const COMPOUNDING_PERIOD_ALIAS = 'compounding_period';
export const COMPOUNDING_PERIOD_TITLE = 'Compounding Periods/Year';
export const COMPOUNDING_PERIOD_ROW_NUMBER = 25;

export const ACCRUED_DIVIDENDS_ALIAS = 'accrued_dividends';
export const ACCRUED_DIVIDENDS_TITLE = 'Accrued Dividends';
export const ACCRUED_DIVIDENDS_ROW_NUMBER = 26;

export const INITIAL_LIQUIDATION_PREFERENCE_ALIAS = 'initial_liquidation_pref';
export const INITIAL_LIQUIDATION_PREFERENCE_TITLE = 'Initial Liquidation Preference';
export const INITIAL_LIQUIDATION_PREFERENCE_ROW_NUMBER = 27;

export const TOTAL_PREFERENCE_ALIAS = 'total_preference';
export const TOTAL_PREFERENCE_TITLE = 'Total Preference';
export const TOTAL_PREFERENCE_ROW_NUMBER = 28;

export const MULTIPLE_INVESTMENT_LEDGER_ALIAS = 'multiple_investments';
export const MULTIPLE_INVESTMENT_LEDGER_TITLE = 'Multiple Investments Ledger';
export const MULTIPLE_INVESTMENT_LEDGER_ROW_NUMBER = 29;

export const OPTIONS_LEDGER_ALIAS = 'options_ledger';
export const OPTIONS_LEDGER_TITLE = 'Options Ledger';
export const OPTIONS_LEDGER_ROW_NUMBER = 30;

export const SECURITY_REFERENCE_ALIAS = 'security_ref';
export const SECURITY_REFERENCE_ALIAS_ROW_NUMBER = 31;

export const CONVERTIBLE_NOTES_ALIAS = 'convertible_notes';
export const CONVERTIBLE_NOTES_TITLE = 'Notes';
export const CONVERTIBLE_NOTES_ROW_NUMBER = 32;

export const TOTAL_NOTE_PRINCIPAL_ALIAS = 'total_note_preference';
export const TOTAL_NOTE_PRINCIPAL_TITLE = 'Total Note Principal';
export const TOTAL_NOTE_PRINCIPAL_ROW_NUMBER = 33;

export const TOTAL_NOTE_VALUE_AT_MD_ALIAS = 'total_note_value_at_md';
export const TOTAL_NOTE_VALUE_AT_MD_TITLE = 'Total Note Value at Measurement Date';
export const TOTAL_NOTE_VALUE_AT_MD_ROW_NUMBER = 34;

export const LIQUIDATION_PREFERENCE_LEDGER_ALIAS = 'liquidation_preference_ledger_config';
export const LIQUIDATION_PREFERENCE_LEDGER_TITLE = 'Liquidation Preference Ledger';
export const LIQUIDATION_PREFERENCE_LEDGER_ROW_NUMBER = 35;

export const DIVIDEND_RATE_LEDGER_ALIAS = 'dividend_rate_ledger_config';
export const DIVIDEND_RATE_LEDGER_TITLE = 'Dividend Rate Ledger';
export const DIVIDEND_RATE_LEDGER_ROW_NUMBER = 36;

export const DIVIDER_BLANK_ROW_ALIAS = 'divider_blank_row';
export const DIVIDER_BLANK_ROW_TITLE = '';

export const TOTAL_SHARES_FULLY_DILUTED_ALIAS = 'shares_diluted';
export const TOTAL_SHARES_FULLY_DILUTED_TITLE = 'Total Shares Fully Diluted';

export const DIVIDEND_RATES = 'dividend_rate_ledger_items';

type EmptyColumnsProps = { tableName: string; columnName: string };
export const EMPTY_COLUMNS = ({ tableName, columnName }: EmptyColumnsProps) =>
  `The ${tableName} has no ${columnName} yet`;
export const CAP_TABLE_NEEDS_MORE_SECURITIES_TITLE = 'Cap Table needs more Securities';
export const CAP_TABLE_NEEDS_MORE_SECURITIES_TAGLINE =
  'Please add at least one Security with type Preferred or Common to generate Breakpoints';
export const TABLE_EMPTY = 'Table Empty';

export const FORBIDDEN_BP_ANALYSIS = 'You do not have permissions to view this breakpoint analysis';
export const GET_BP_ACCESS = 'Please contact your manager to gain access';

export const DEFAULT_CURRENCY = 'USD';

export const CAP_TABLE_LOCKED_MESSAGE =
  'This Cap Table version is used in a Final Valuation, any changes saved could affect that valuation.';

export const BREAKPOINT_ANALYSIS_LOCKED_MESSAGE =
  'This Breakpoint Analysis is being used on a Final Valuation, any changes saved could affect that valuation.';

export const CASH_FLOW_LEDGER_LOCKED_MESSAGE =
  'This Cash Flow Ledger is being used on a Final Valuation, any changes saved could affect that valuation.';

export const SECURITY_SELECTED_IN_FO_OR_BACKSOLVE =
  'Changing this security\'s type is not allowed while it is selected in a fund ownership and/or backsolve';

export const SECURITY_SELECTION_DISABLED_BECAUSE_OF_FO =
  'The security type can\'t be modified when ownership exists for this security. To change it, first delete the associated fund(s) ownership.';

export const CONVERSION_MODEL_DISABLED_BECAUSE_OF_FO =
  'The conversion model can\'t be modified when ownership exists for this note. To change it, first delete the associated fund(s) ownership.';

export const TITLE_TO_ALIAS = {
  [SECURITY_NAME_TITLE]: SECURITY_NAME_ALIAS,
  [LEGAL_NAME_TITLE]: LEGAL_NAME_ALIAS,
  [INVESTMENT_DATE_TITLE]: INVESTMENT_DATE_ALIAS,
  [SECURITY_TYPE_TITLE]: SECURITY_TYPE_ALIAS,
  [ISSUE_PRICE_TITLE]: ISSUE_PRICE_ALIAS,
  [SHARES_OUTSTANDING_TITLE]: SHARES_OUTSTANDING_ALIAS,
  [CONVERSION_RATE_TITLE]: CONVERSION_RATE_ALIAS,
  [PIK_SHARES_TITLE]: PIK_SHARES_ALIAS,
  [SHARES_FULLY_DILUTED_TITLE]: SHARES_FULLY_DILUTED_ALIAS,
  [CURRENT_OWNERSHIP_TITLE]: CURRENT_OWNERSHIP_ALIAS,
  [FULLY_DILUTED_OWNERSHIP_TITLE]: FULLY_DILUTED_OWNERSHIP_ALIAS,
  [STRIKE_PRICE_TITLE]: STRIKE_PRICE_ALIAS,
  [UNDERLYING_SECURITY_TITLE]: UNDERLYING_SECURITY_ALIAS,
  [PREFERRED_TERMS_TITLE]: PREFERRED_TERMS_ALIAS,
  [LIQUIDATION_PREFERENCE_TITLE]: LIQUIDATION_PREFERENCE_ALIAS,
  [SENIORITY_TITLE]: SENIORITY_ALIAS,
  [PARTICIPATION_TITLE]: PARTICIPATION_ALIAS,
  [IS_PARTICIPATION_CAP_TITLE]: IS_PARTICIPATION_CAP_ALIAS,
  [PARTICIPATION_CAP_TITLE]: PARTICIPATION_CAP_ALIAS,
  [CUMULATIVE_DIVIDENDS_TITLE]: CUMULATIVE_DIVIDENDS_ALIAS,
  [HAS_MULTIPLE_INVESTMENT_DATES_TITLE]: HAS_MULTIPLE_INVESTMENT_DATES_ALIAS,
  [DIVIDEND_PAYMENT_TYPE_TITLE]: DIVIDEND_PAYMENT_TYPE_ALIAS,
  [DIVIDEND_RATE_TITLE]: DIVIDEND_RATE_ALIAS,
  [COMPOUNDING_TITLE]: COMPOUNDING_ALIAS,
  [COMPOUNDING_PERIOD_TITLE]: COMPOUNDING_PERIOD_ALIAS,
  [ACCRUED_DIVIDENDS_TITLE]: ACCRUED_DIVIDENDS_ALIAS,
  [TOTAL_PREFERENCE_TITLE]: TOTAL_PREFERENCE_ALIAS,
  [MULTIPLE_INVESTMENT_LEDGER_TITLE]: MULTIPLE_INVESTMENT_LEDGER_ALIAS,
  [OPTIONS_LEDGER_TITLE]: OPTIONS_LEDGER_ALIAS,
  [TOTAL_SHARES_FULLY_DILUTED_TITLE]: TOTAL_SHARES_FULLY_DILUTED_ALIAS,
};

export const ASSIGNED_TYPE_FOR_NOTES = {
  1: PREFERRED_STOCK,
  2: COMMON_STOCK,
  3: CONVERTIBLE_NOTES,
};

export const INITIAL_CAP_TABLE_STATE = {
  [SECURITY_NAME_ALIAS]: SYSTEM_COLLAPSE,
  [STRIKE_PRICE_ALIAS]: SYSTEM_COLLAPSE,
  [PREFERRED_TERMS_ALIAS]: SYSTEM_COLLAPSE,
};

export const CAP_TABLE_TERMS = {
  tableName: CAP_TABLE_TITLE,
  tableSlug: CAP_TABLE_SLUG,
  columnName: SECURITY,
  pluralColumnName: SECURITIES,
};

export const divRateLedgerInfluencedCellAliases = [
  { rowNumber: DIVIDEND_RATE_ROW_NUMBER, alias: DIVIDEND_RATE_ALIAS },
  { rowNumber: COMPOUNDING_ROW_NUMBER, alias: COMPOUNDING_ALIAS },
  { rowNumber: COMPOUNDING_PERIOD_ROW_NUMBER, alias: COMPOUNDING_PERIOD_ALIAS },
];

export const liqPrefLedgerInfluencedCellAliases = [
  { rowNumber: LIQUIDATION_PREFERENCE_ROW_NUMBER, alias: LIQUIDATION_PREFERENCE_ALIAS },
  { rowNumber: CUMULATIVE_DIVIDENDS_ROW_NUMBER, alias: CUMULATIVE_DIVIDENDS_ALIAS },
  { rowNumber: DIVIDEND_RATE_ROW_NUMBER, alias: DIVIDEND_RATE_ALIAS },
  { rowNumber: COMPOUNDING_ROW_NUMBER, alias: COMPOUNDING_ALIAS },
  { rowNumber: COMPOUNDING_PERIOD_ROW_NUMBER, alias: COMPOUNDING_PERIOD_ALIAS },
];

/** Aliases whose edits should trigger a server-side recalculation of
 *  accrued dividends on securities with multiple investment dates. */
export const RECALCULATE_TRIGGERING_ALIASES: ReadonlySet<string> = new Set([
  ISSUE_PRICE_ALIAS,
  INVESTMENT_DATE_ALIAS,
  LIQUIDATION_PREFERENCE_ALIAS,
  CUMULATIVE_DIVIDENDS_ALIAS, // has_dividends
  DIVIDEND_RATE_ALIAS,
  COMPOUNDING_ALIAS, // has_compounding
  COMPOUNDING_PERIOD_ALIAS,
  SHARES_OUTSTANDING_ALIAS,
]);

// Liquidation Preference Ledger constants
export const LPL_EFFECTIVE_DATE_TITLE = 'Effective Date';
export const LPL_EFFECTIVE_DATE_ALIAS = 'effective_date';
export const LPL_EFFECTIVE_DATE_ROW_NUMBER = 2;

// Dividend Rate Ledger constants
export const DRL_NAME_TITLE = 'Name';
export const DRL_NAME_ALIAS = 'name';
export const DRL_EFFECTIVE_DATE_TITLE = 'Effective Date';
export const DRL_EFFECTIVE_DATE_ALIAS = 'effective_date';
export const DRL_EFFECTIVE_DATE_ROW_NUMBER = 2;
export const DRL_DIVIDEND_RATE_TITLE = 'Dividend Rate';
export const DRL_DIVIDEND_RATE_ALIAS = 'dividend_rate';
export const DRL_COMPOUNDING_TITLE = 'Compounding';
export const DRL_COMPOUNDING_ALIAS = 'has_compounding';
export const DRL_COMPOUNDING_ROW_NUMBER = 4;
export const DRL_COMPOUNDING_PERIODS_TITLE = 'Compounding Periods/Year';
export const DRL_COMPOUNDING_PERIODS_ALIAS = 'compounding_periods';
export const DRL_COMPOUNDING_PERIODS_ROW_NUMBER = 5;

export const booleanFriendlyOptions = [
  { value: 0, label: 'No' },
  { value: 1, label: 'Yes' },
];

export const CAP_TABLE_ROW_GROUPS_PREFERENCES_KEY = 'capTableRowGroupsPreferences';

// New round modeler
export const OPR_DECIMAL_PLACES = 4;
export const SERIES_NAME_PREFIX = 'Series ';
export const FIRST_SERIES_LETTER = 'A';
export const DEFAULT_SERIES_NAME = `${SERIES_NAME_PREFIX}${FIRST_SERIES_LETTER}`;
export const DEFAULT_FALLBACK_VALUE = '0';
export const EMPTY_DISPLAY_VALUE = '—';
export const EMPTY_OPTION_POOL_REFRESH = '0.00';
export const POOL_REFRESH_MODE_PRE = 'pre';
export const POOL_REFRESH_MODE_POST = 'post';
export const POOL_REFRESH_MODE_PRE_LABEL = 'Pre-Money';
export const POOL_REFRESH_MODE_POST_LABEL = 'Post-Money';
export const NEW_ROUND_PERCENT_SUFFIX = '%';

export const NEW_ROUND_MODELER_TITLE = 'NEW ROUND MODELER';
export const NEW_ROUND_CURRENCY_BADGE_LABEL = 'USD';
export const NEW_ROUND_NUMBER_FORMAT_BADGE_LABEL = '($)';
export const NEW_ROUND_CURRENCY_BADGE_TOOLTIP =
  'The New Round Modeler always calculates in USD, regardless of this cap table\'s currency.';
export const NEW_ROUND_MODELER_DESCRIPTION =
  'The New Round Modeler generates a proforma cap table incorporating an additional preferred security with identical terms to the most recent preferred security. This proforma version also replicates existing fund ownership, incorporating new ownership based on the provided inputs. This new version has no effect on existing valuations or fund conclusions.';
export const NEW_ROUND_NO_PREFERRED_SECURITY_ERROR =
  'This cap table has no existing preferred security to clone terms from, so the New Round Modeler cannot be used here.';
export const getNewRoundSecurityNameTakenMessage = (name: string): string =>
  `"${name}" is already used by an existing security. Choose a different New Security Name.`;
export const NEW_ROUND_FUND_INVESTMENT_EXCEEDS_AMOUNT_ERROR =
  '$ Fund Investment in New Round cannot exceed Amount Invested (New Round).';

export const NEW_ROUND_INPUTS_HEADER = 'New Round Inputs';
export const NEW_ROUND_FUND_OWNERSHIP_HEADER = 'New Round Fund Ownership';
export const NEW_ROUND_CALCULATIONS_HEADER = 'New Round Calculations';

export const NEW_ROUND_PRE_MONEY_VALUATION_LABEL = 'Pre-Money Valuation';
export const NEW_ROUND_PRE_MONEY_VALUATION_TOOLTIP = 'Total pre-money valuation of the company before the new round';
export const NEW_ROUND_AMOUNT_INVESTED_LABEL = 'Amount Invested (New Round)';
export const NEW_ROUND_OPTION_POOL_REFRESH_LABEL = 'Option Pool Refresh';
export const NEW_ROUND_OPTION_POOL_REFRESH_TOOLTIP =
  'Target unallocated option pool as % of post-money fully diluted shares. Defaults to current unallocated pool %.';
export const NEW_ROUND_POOL_REFRESH_MODE_LABEL = 'Pre or Post Money Option Pool Refresh';
export const NEW_ROUND_POOL_REFRESH_MODE_TOOLTIP =
  'Pre-Money: option pool expanded before new shares issued (existing holders bear dilution). Post-Money: pool expanded after new shares issued (all holders bear dilution).';
export const NEW_ROUND_SECURITY_NAME_LABEL = 'New Security Name';
export const NEW_ROUND_SECURITY_NAME_TOOLTIP = 'Name for the new preferred security. Defaults to the next alphabetical Series name.';
export const NEW_ROUND_INVESTMENT_DATE_LABEL = 'New Round Investment Date';
export const NEW_ROUND_INVESTMENT_DATE_TOOLTIP = 'Investment date for the new round. Defaults to today.';
export const NEW_ROUND_FUND_LABEL = 'Fund';
export const NEW_ROUND_FUND_INVESTMENT_LABEL = '$ Fund Investment in New Round';
export const NEW_ROUND_FUND_INVESTMENT_TOOLTIP = 'Amount the fund is investing in the new round. Defaults to the total amount invested.';
export const NEW_ROUND_FUND_OWNERSHIP_PCT_LABEL = '% Fund Ownership of New Round';
export const NEW_ROUND_NUMBER_OF_SHARES_LABEL = 'Number of Shares';
export const NEW_ROUND_NUMBER_OF_SHARES_TOOLTIP = 'Number of shares purchased by the fund in the new round';

export const NEW_ROUND_CURRENT_SHARES_OUTSTANDING_LABEL = 'Current Shares Outstanding';
export const NEW_ROUND_CURRENT_SHARES_OUTSTANDING_TOOLTIP =
  'Fully diluted shares from the current cap table, excluding unallocated option pool';
export const NEW_ROUND_PREMONEY_UNALLOCATED_OPTIONS_LABEL = 'Pre-Money Unallocated Options';
export const NEW_ROUND_PREMONEY_UNALLOCATED_OPTIONS_TOOLTIP = 'Unallocated option pool shares from the current cap table';
export const NEW_ROUND_PREMONEY_FDS_LABEL = 'Pre-Money Fully Diluted Shares';
export const NEW_ROUND_POOL_REFRESH_FACTOR_LABEL = 'Pool Refresh Factor';
export const NEW_ROUND_INCREMENTAL_OPTION_POOL_SHARES_LABEL = 'Incremental Option Pool Shares';
export const NEW_ROUND_ADJUSTED_PREMONEY_FDS_LABEL = 'Adjusted Pre-Money Fully Diluted Shares';
export const NEW_ROUND_ADJUSTED_PREMONEY_FDS_TOOLTIP = 'Pre-Money Fully Diluted Shares including refreshed option pool';
export const NEW_ROUND_ISSUE_PRICE_LABEL = 'New Round Issue Price';
export const NEW_ROUND_ISSUE_PRICE_PRE_TOOLTIP =
  'Pre-Money Valuation ÷ Adjusted Pre-Money Fully Diluted Shares (with refreshed option pool)';
export const NEW_ROUND_ISSUE_PRICE_POST_TOOLTIP = 'Pre-Money Valuation ÷ Pre-Money Fully Diluted Shares';
export const NEW_ROUND_SHARES_ISSUED_LABEL = 'New Round Shares Issued';
export const NEW_ROUND_SHARES_ISSUED_TOOLTIP = 'Amount Invested ÷ New Round Issue Price';
export const NEW_ROUND_POST_MONEY_FDS_LABEL = 'Post-Money Fully Diluted Shares';
export const NEW_ROUND_POST_MONEY_FDS_PRE_TOOLTIP = 'Adjusted Pre-Money FDS + New Round Shares Issued';
export const NEW_ROUND_POST_MONEY_FDS_POST_TOOLTIP =
  'Pre-Money FDS + New Round Shares Issued + Incremental Option Pool Shares';
export const NEW_ROUND_POST_MONEY_VALUATION_LABEL = 'Post-Money Valuation';
export const NEW_ROUND_DILUTION_LABEL = 'Dilution to Existing Holders';
export const NEW_ROUND_DILUTION_TOOLTIP = 'Pre-Money Fully Diluted Shares / Post-Money Fully Diluted Shares − 1';
export const NEW_ROUND_PRF_PRE_TOOLTIP = 'k = Option Pool Refresh % × (1 + Amount Invested / Pre-Money Valuation)';
export const NEW_ROUND_PRF_POST_TOOLTIP =
  'Option Pool Refresh % (target unallocated pool as % of post-money fully diluted shares)';
